import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Sparkles,
  Layers,
  Gauge,
  Wrench,
  Factory,
  Globe,
  ShieldCheck,
  ArrowRight,
  Award,
  CheckCircle2,
  Clock,
  Truck,
  Bot,
} from "lucide-react";
import PipeClampFinder from "../components/PipeClampFinder";
import ProductCard from "../components/ProductCard";
import WeekknallerSpotlight from "../components/WeekknallerSpotlight";
import { fetchProducts, fetchIndustries, fetchCategories } from "../lib/api";

export default function HomePage() {
  const { t } = useTranslation();
  const [products, setProducts] = useState([]);
  const [industries, setIndustries] = useState([]);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    fetchProducts().then((d) => setProducts(d.slice(0, 8))).catch(() => {});
    fetchIndustries().then(setIndustries).catch(() => {});
    fetchCategories().then(setCategories).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-white text-zinc-900" data-testid="home-page">
      {/* HERO — white + sage-green */}
      <section className="relative overflow-hidden">
        {/* soft blueprint pattern */}
        <div
          className="absolute inset-0 opacity-50"
          style={{
            backgroundImage:
              "linear-gradient(rgba(157,186,161,0.10) 1px, transparent 1px), linear-gradient(90deg, rgba(157,186,161,0.10) 1px, transparent 1px)",
            backgroundSize: "48px 48px",
          }}
        />
        <div className="absolute -right-32 top-0 h-[400px] w-[400px] rounded-full bg-nibro-sage-pale blur-[80px]" />
        <div className="absolute -left-20 bottom-0 h-[300px] w-[300px] rounded-full bg-nibro-sage-pale blur-[60px]" />

        <div className="relative mx-auto grid max-w-[1400px] grid-cols-1 gap-12 px-5 py-20 lg:grid-cols-2 lg:gap-16 lg:px-10 lg:py-28">
          <div className="flex flex-col justify-center">
            <div className="mb-6 inline-flex items-center gap-2 self-start rounded-full border border-aramfix-red/30 bg-white px-3 py-1.5 shadow-sm">
              <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-aramfix-red" />
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-700">
                {t("home.hero_kicker")}
              </span>
            </div>

            <h1 className="font-display text-5xl font-bold leading-[0.95] tracking-tighter text-zinc-900 sm:text-6xl lg:text-7xl">
              {t("home.hero_title_line1")} <span className="italic font-light text-nibro-sage-dark">{t("home.hero_title_emphasis")}</span>
              <br />
              {t("home.hero_title_line2")}
              <br />
              <span className="text-nibro-sage-dark">{t("home.hero_title_line3")}</span>
            </h1>

            <p className="mt-6 max-w-xl text-lg leading-relaxed text-zinc-600">
              {t("home.hero_subtitle")}
            </p>

            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/products"
                className="group inline-flex items-center gap-2 rounded-md bg-nibro-sage px-6 py-3.5 text-sm font-bold text-white transition hover:bg-nibro-sage-hover"
                data-testid="hero-find-clamp-button"
              >
                {t("home.hero_cta_catalog")} <ArrowRight size={16} className="transition group-hover:translate-x-1" />
              </Link>
              <Link
                to="/configurator"
                className="inline-flex items-center gap-2 rounded-md border-2 border-nibro-sage bg-white px-6 py-3.5 text-sm font-bold text-zinc-900 transition hover:bg-nibro-sage-pale"
                data-testid="hero-configurator-button"
              >
                <Wrench size={16} className="text-nibro-sage-dark" /> {t("home.hero_cta_configurator")}
              </Link>
            </div>

            <div className="mt-12 grid grid-cols-3 gap-6 border-t border-nibro-sage/30 pt-6">
              <Stat label={t("home.stat_dn_sizes")} value="6–300" />
              <Stat label={t("home.stat_in_stock")} value="24h" />
              <Stat label={t("home.stat_materials")} value="304 · 316 · Duplex" />
            </div>
          </div>

          {/* Right column: clamp on top, finder below */}
          <div className="relative flex flex-col items-center justify-center">
            {/* Eye-catcher polished clamp — ABOVE the finder */}
            <img
              src="/nibro-clamp-hero.png"
              alt="NIBRO polished stainless steel pipe clamp"
              className="pointer-events-none relative z-0 mb-[-40px] hidden w-full max-w-[680px] select-none object-contain drop-shadow-[0_30px_80px_rgba(74,99,77,0.45)] lg:block xl:max-w-[760px] 2xl:mb-[-50px] 2xl:max-w-[860px]"
              data-testid="hero-showcase-clamp"
            />
            {/* Rich sage glow disc behind the clamp */}
            <div
              aria-hidden="true"
              className="pointer-events-none absolute inset-0 -z-10 hidden lg:block"
            >
              <div className="absolute left-1/2 top-[15%] h-[500px] w-[700px] -translate-x-1/2 rounded-full bg-nibro-sage-pale blur-3xl opacity-75" />
            </div>

            <div className="relative z-10 w-full">
              <PipeClampFinder />
              {/* Floating product image bottom-left of finder */}
              <div className="absolute -bottom-16 -left-16 hidden h-28 w-28 overflow-hidden rounded-xl border border-nibro-sage/40 bg-white shadow-xl xl:block">
                <img
                  src="https://nibrobv.nl/wp-content/uploads/2020/02/Clamp_DIN3567-scaled.jpg"
                  alt="Pipe clamp close-up"
                  className="h-full w-full object-contain p-2"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TRUST STRIP — full-width sage-green bar */}
      <section
        className="border-y border-nibro-sage/30"
        style={{ backgroundColor: "var(--nibro-sage-pale)" }}
      >
        <div className="mx-auto flex max-w-[1400px] flex-wrap items-center justify-between gap-y-4 px-5 py-6 lg:px-10">
          <TrustItem icon={<ShieldCheck size={16} />} text={t("home.trust_certified")} />
          <TrustItem icon={<Factory size={16} />} text={t("home.trust_made_in")} />
          <TrustItem icon={<Globe size={16} />} text={t("home.trust_worldwide")} />
          <TrustItem icon={<Gauge size={16} />} text={t("home.trust_live_engine")} />
          <TrustItem icon={<Bot size={16} />} text={t("home.trust_ai")} />
        </div>
      </section>

      {/* WEEKKNALLER SPOTLIGHT */}
      <WeekknallerSpotlight />

      {/* PRODUCT RANGE */}
      <section className="mx-auto max-w-[1400px] px-5 py-20 lg:px-10">
        <div className="mb-12 flex items-end justify-between gap-4">
          <div>
            <div className="label-uppercase mb-3">{t("home.range_kicker")}</div>
            <h2 className="font-display text-4xl font-bold tracking-tighter text-zinc-900 sm:text-5xl">
              {t("home.range_title_line1")}<br />
              <span className="text-nibro-sage-dark">{t("home.range_title_line2")}</span>
            </h2>
          </div>
          <Link
            to="/products"
            className="hidden items-center gap-2 text-sm font-semibold text-nibro-sage-dark transition hover:text-nibro-sage-darker md:flex"
            data-testid="categories-view-all"
          >
            {t("home.range_view_all")} <ArrowRight size={14} />
          </Link>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {categories.map((c) => (
            <Link
              key={c.name}
              to={`/products?category=${encodeURIComponent(c.name)}`}
              className="group relative overflow-hidden rounded-md border border-nibro-sage/30 bg-white p-6 transition hover:border-nibro-sage hover:shadow-lg"
              data-testid={`category-card-${c.name.toLowerCase().replace(/\s/g, "-")}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex h-12 w-12 items-center justify-center rounded-md bg-nibro-sage-pale text-nibro-sage-dark transition group-hover:bg-nibro-sage group-hover:text-white">
                  <Layers size={24} />
                </div>
                <span className="font-mono text-[10px] uppercase tracking-wider text-zinc-400">
                  {c.count} {t("home.range_items")}
                </span>
              </div>
              <div className="mt-10 font-display text-xl font-bold leading-tight text-zinc-900">
                {c.name}
              </div>
              <div className="mt-2 flex items-center gap-1 text-sm font-semibold text-nibro-sage-dark">
                {t("home.range_browse")} <ArrowRight size={14} className="transition group-hover:translate-x-1" />
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* FEATURED PRODUCTS */}
      <section
        className="border-y border-nibro-sage/20"
        style={{ backgroundColor: "var(--nibro-sage-pale)" }}
      >
        <div className="mx-auto max-w-[1400px] px-5 py-20 lg:px-10">
          <div className="mb-10 flex items-end justify-between gap-4">
            <div>
              <div className="label-uppercase mb-3">{t("home.featured_kicker")}</div>
              <h2 className="font-display text-3xl font-bold tracking-tighter text-zinc-900 sm:text-4xl">
                {t("home.featured_title")}
              </h2>
            </div>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {products.map((p) => (
              <ProductCard key={p.id} p={p} />
            ))}
          </div>
          <div className="mt-10 flex justify-center">
            <Link
              to="/products"
              className="inline-flex items-center gap-2 rounded-md bg-nibro-sage px-6 py-3 text-sm font-bold text-white transition hover:bg-nibro-sage-hover"
              data-testid="view-all-products"
            >
              {t("home.range_view_all")} <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* PERFECTLY STRAIGHT — international brand positioning */}
      <section
        className="border-y-2 border-aramfix-red/30"
        style={{ backgroundColor: "var(--nibro-sage-dark)" }}
      >
        <div className="mx-auto max-w-[1400px] px-5 py-20 lg:px-10">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-[1.4fr_1fr] lg:items-center">
            <div className="text-white">
              <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-aramfix-red">
                {t("home.ps_kicker")}
              </div>
              <h2 className="mt-3 font-display text-4xl font-bold leading-[1.05] tracking-tight sm:text-5xl">
                {t("home.ps_title_line1")}<br />
                <span className="text-aramfix-red">{t("home.ps_title_line2")}</span>
              </h2>
              <p className="mt-5 max-w-2xl text-lg leading-relaxed text-white/85">
                {t("home.ps_body")}
              </p>
              <ul className="mt-6 grid grid-cols-1 gap-2.5 sm:grid-cols-2">
                {[
                  t("home.ps_b1"),
                  t("home.ps_b2"),
                  t("home.ps_b3"),
                  t("home.ps_b4"),
                ].map((it, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-white/90">
                    <span className="mt-1 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-aramfix-red" />
                    {it}
                  </li>
                ))}
              </ul>
              <div className="mt-7 flex flex-wrap gap-3">
                <Link
                  to="/landing/why-perfectly-straight-matters"
                  className="flex items-center gap-2 rounded-md bg-aramfix-red px-6 py-3 text-sm font-bold text-white transition hover:bg-aramfix-red/90"
                  data-testid="home-perfectly-straight-cta"
                >
                  {t("home.ps_cta_case")} <ArrowRight size={14} />
                </Link>
                <Link
                  to="/contact?type=oem"
                  className="flex items-center gap-2 rounded-md border-2 border-white/40 bg-transparent px-6 py-3 text-sm font-bold text-white transition hover:bg-white/10"
                >
                  {t("home.ps_cta_oem")}
                </Link>
              </div>
            </div>
            <div className="rounded-lg border-2 border-aramfix-red/30 bg-white/5 p-7 backdrop-blur">
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/60">
                {t("home.ps_tolerance")}
              </div>
              <div className="mt-2 font-display text-6xl font-bold text-white">
                &lt; 0.3<span className="text-2xl"> mm</span>
              </div>
              <div className="mt-1 text-sm text-white/70">{t("home.ps_tolerance_caption")}</div>

              <div className="my-6 h-px w-full bg-white/15" />

              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/60">
                {t("home.ps_grades")}
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                {["304 / V2A", "316L / V4A", "Duplex 1.4462", "EPDM lined", "Silicone"].map((m) => (
                  <span key={m} className="rounded-full border border-white/30 bg-white/5 px-3 py-1 font-mono text-[11px] text-white">
                    {m}
                  </span>
                ))}
              </div>

              <div className="my-6 h-px w-full bg-white/15" />

              <div className="grid grid-cols-3 gap-3">
                {[
                  { v: "1300+", l: "SKUs" },
                  { v: "24h", l: "Dispatch" },
                  { v: "22", l: "Countries" },
                ].map((s) => (
                  <div key={s.l}>
                    <div className="font-display text-2xl font-bold text-white">{s.v}</div>
                    <div className="font-mono text-[10px] uppercase tracking-wider text-white/60">{s.l}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* NIBRO STAAT VOOR / USPs */}
      <section className="mx-auto max-w-[1400px] px-5 py-20 lg:px-10">
        <div className="mb-12 text-center">
          <div className="label-uppercase mb-3">{t("home.why_kicker")}</div>
          <h2 className="font-display text-3xl font-bold tracking-tighter text-zinc-900 sm:text-4xl">
            {t("home.why_title")}
          </h2>
        </div>
        <div className="grid grid-cols-2 gap-6 md:grid-cols-3 lg:grid-cols-5">
          <Usp icon={<Award size={28} />} title={t("home.usp_value")} />
          <Usp icon={<CheckCircle2 size={28} />} title={t("home.usp_material")} />
          <Usp icon={<Clock size={28} />} title={t("home.usp_delivery")} />
          <Usp icon={<Wrench size={28} />} title={t("home.usp_custom")} />
          <Usp icon={<Layers size={28} />} title={t("home.usp_technique")} />
        </div>
      </section>

      {/* INDUSTRIES */}
      <section
        className="border-y border-nibro-sage/20"
        style={{ backgroundColor: "var(--nibro-sage-pale)" }}
      >
        <div className="mx-auto max-w-[1400px] px-5 py-20 lg:px-10">
          <div className="mb-10 flex items-end justify-between gap-4">
            <div>
              <div className="label-uppercase mb-3">{t("home.industries_kicker")}</div>
              <h2 className="font-display text-3xl font-bold tracking-tighter text-zinc-900 sm:text-4xl">
                {t("home.industries_title")}
              </h2>
            </div>
            <Link
              to="/industries"
              className="hidden items-center gap-2 text-sm font-semibold text-nibro-sage-dark transition hover:text-nibro-sage-darker md:flex"
            >
              {t("home.industries_view_all")} <ArrowRight size={14} />
            </Link>
          </div>
          <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4">
            {industries.slice(0, 8).map((i) => (
              <Link
                key={i.slug}
                to={`/industries/${i.slug}`}
                className="group relative aspect-[4/5] overflow-hidden rounded-md border border-nibro-sage/30 bg-white"
                data-testid={`industry-card-${i.slug}`}
              >
                <img
                  src={i.image}
                  alt={i.name}
                  className="h-full w-full object-cover transition duration-700 group-hover:scale-105"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-5">
                  <div className="font-display text-lg font-bold uppercase tracking-wide text-white">
                    {i.name}
                  </div>
                  <div className="mt-1 font-mono text-[10px] uppercase tracking-wider text-white/85 opacity-0 transition group-hover:opacity-100">
                    {t("home.industries_view_solutions")}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA — Custom Configurator */}
      <section className="mx-auto max-w-[1400px] px-5 py-20 lg:px-10">
        <div className="relative overflow-hidden rounded-md border-2 border-nibro-sage bg-white p-10 lg:p-16">
          <div className="absolute -right-20 -top-20 h-72 w-72 rounded-full bg-nibro-sage-pale blur-[80px]" />
          <div className="relative grid grid-cols-1 gap-10 lg:grid-cols-2 lg:gap-16">
            <div>
              <div className="label-uppercase mb-4">{t("home.config_kicker")}</div>
              <h2 className="font-display text-4xl font-bold leading-tight tracking-tighter text-zinc-900 sm:text-5xl">
                {t("home.config_title_line1")}<br />
                <span className="text-nibro-sage-dark">{t("home.config_title_line2")}</span>
              </h2>
              <p className="mt-5 max-w-lg text-zinc-600">
                {t("home.config_body")}
              </p>
              <Link
                to="/configurator"
                className="mt-8 inline-flex items-center gap-2 rounded-md bg-nibro-sage px-6 py-3.5 text-sm font-bold text-white transition hover:bg-nibro-sage-hover"
                data-testid="cta-configurator-link"
              >
                {t("home.config_cta")} <ArrowRight size={16} />
              </Link>
            </div>
            <div className="relative aspect-square overflow-hidden rounded-md border border-nibro-sage/30 bg-nibro-sage-pale">
              <img
                src="https://nibrobv.nl/wp-content/uploads/2020/02/Clamp_DIN3567-scaled.jpg"
                alt="Custom configurator"
                className="h-full w-full object-contain p-12"
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div className="label-uppercase mb-1">{label}</div>
      <div className="font-display text-xl font-bold text-zinc-900">{value}</div>
    </div>
  );
}

function TrustItem({ icon, text }) {
  return (
    <div className="flex items-center gap-2 text-sm">
      <span className="text-nibro-sage-dark">{icon}</span>
      <span className="font-mono text-[11px] font-semibold uppercase tracking-[0.15em] text-zinc-700">
        {text}
      </span>
    </div>
  );
}

function Usp({ icon, title }) {
  return (
    <div className="flex flex-col items-center text-center">
      <div className="flex h-14 w-14 items-center justify-center rounded-full bg-nibro-sage text-white">
        {icon}
      </div>
      <div className="mt-3 max-w-[180px] font-display text-sm font-semibold uppercase tracking-wide text-zinc-800">
        {title}
      </div>
    </div>
  );
}
